<template>
  <main v-if="$parent.isAuth" class="main cart-page page-inside">
    <div class="main-wrapper">
      <header class="section hero-section">
        <div class="wrapper">
          <div class="wrapper-top">
            <div class="title-container">
              <button
                type="button"
                class="back-page"
                :aria-label="$t('Назад')"
                @click="$parent.back"
              >
                <img class="back-img" src="./../img/back-page.svg" alt="" />
              </button>

              <h1 class="cart-page__title">
                {{ $t('Моя корзина') }}
              </h1>
            </div>

            <button
              v-if="hasCartItems"
              type="button"
              class="button bordered small clear-cart-button"
              @click="$parent.emptyCart"
            >
              <span>{{ $t('ОЧИСТИТЬ КОРЗИНУ') }}</span>
            </button>
          </div>
        </div>
      </header>

      <section class="section cart-section">
        <div class="wrapper">
          <div v-if="!hasCartItems" class="empty-cart">
            <div class="empty-cart__icon">
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 7H9L12.5 25H30L34 12H11"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />

                <circle cx="15" cy="32" r="2" fill="currentColor" />

                <circle cx="29" cy="32" r="2" fill="currentColor" />
              </svg>
            </div>

            <h2 class="empty-cart__title">
              {{ $t('Корзина пуста') }}
            </h2>

            <p class="empty-cart__description">
              {{ $t('Добавьте товары из магазина, и они появятся здесь.') }}
            </p>

            <button
              type="button"
              class="button shadowed empty-cart__button"
              @click="goToStore"
            >
              <span>{{ $t('ПЕРЕЙТИ В МАГАЗИН') }}</span>
            </button>
          </div>

          <template v-else>
            <aside class="cart-summary">
              <div class="cart-summary__card">
                <div class="cart-summary__heading">
                  <h2 class="cart-summary__title">
                    {{ $t('Ваш заказ') }}
                  </h2>

                  <span class="cart-summary__count">
                    {{ cartItemsCount }}
                  </span>
                </div>

                <div class="cart-summary__total">
                  <span class="cart-summary__total-label">
                    {{ $t('Итого') }}
                  </span>

                  <div class="cart-summary__total-price">
                    <span>{{ $parent.totalSum }}</span>

                    <span class="currency">
                      {{ currency }}
                    </span>
                  </div>

                  <div
                    v-if="availablePaymentMethods.length"
                    class="payment-methods"
                  >
                    <div class="payment-methods__title">
                      {{ $t('Способ оплаты') }}
                    </div>

                    <div class="payment-methods__list">
                      <button
                        v-for="method in availablePaymentMethods"
                        :key="method.id"
                        type="button"
                        class="payment-method"
                        :class="{
                          'payment-method_active':
                            selectedPaymentMethod === method.code,
                        }"
                        @click="selectedPaymentMethod = method.code"
                      >
                        {{ method.text }}
                      </button>
                    </div>
                  </div>

                  <div v-else class="cart-message cart-message_error">
                    {{ $t('Нет доступных способов оплаты') }}
                  </div>
                </div>

                <div class="cart-summary__terms">
                  <div
                    v-if="textPagesLoading"
                    class="terms-state terms-state_loading"
                    role="status"
                    aria-live="polite"
                  >
                    <span class="terms-state__loader" aria-hidden="true"></span>

                    <span>
                      {{ $t('Загрузка документов...') }}
                    </span>
                  </div>

                  <div
                    v-else-if="!documentsAreAvailable"
                    class="terms-state terms-state_error"
                    role="alert"
                  >
                    <span class="terms-state__icon" aria-hidden="true">
                      !
                    </span>

                    <div class="terms-state__content">
                      <span class="terms-state__message">
                        {{
                          textPagesError ||
                          $t('Условия использования временно недоступны')
                        }}
                      </span>

                      <button
                        type="button"
                        class="terms-state__retry"
                        @click="retryTextPages"
                      >
                        {{ $t('Повторить') }}
                      </button>
                    </div>
                  </div>

                  <label v-else class="terms-checkbox">
                    <input v-model="terms" type="checkbox" name="terms" />

                    <span class="terms-checkbox__box">
                      <svg
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3 8.5L6.3 11.5L13 4.5"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>

                    <span class="terms-checkbox__text">
                      {{ $t('Я принимаю') }}

                      <button
                        type="button"
                        class="terms-checkbox__link"
                        @click.prevent.stop="openStaticPage(termsPage)"
                      >
                        {{ termsPage.title }}
                      </button>

                      {{ $t('и') }}

                      <button
                        type="button"
                        class="terms-checkbox__link"
                        @click.prevent.stop="openStaticPage(privacyPage)"
                      >
                        {{ privacyPage.title }}
                      </button>
                    </span>
                  </label>
                </div>

                <button
                  type="button"
                  class="button shadowed cart-summary__submit"
                  :disabled="!requiredFieldsAreFilled || checkoutLoading"
                  @click="submit"
                >
                  <span>{{ $t('ОФОРМИТЬ ЗАКАЗ') }}</span>
                </button>

                <div class="promo">
                  <label for="cart-promocode" class="promo__label">
                    {{ $t('Промокод') }}
                  </label>

                  <div class="promo__controls">
                    <input
                      id="cart-promocode"
                      v-model.trim="promo"
                      type="text"
                      class="promo__input"
                      :placeholder="$t('Введите промокод')"
                      @keyup.enter="applyPromo"
                    />

                    <button
                      type="button"
                      class="button bordered promo__button"
                      :disabled="!promo"
                      @click="applyPromo"
                    >
                      <span>{{ $t('ПРИМЕНИТЬ') }}</span>
                    </button>
                  </div>
                </div>

                <transition name="slide">
                  <div
                    v-if="promoError"
                    class="cart-message cart-message_error"
                  >
                    {{ promoError }}
                  </div>
                </transition>

                <transition name="slide">
                  <div
                    v-if="$parent.error"
                    class="cart-message cart-message_error"
                  >
                    {{ $parent.error }}
                  </div>
                </transition>
              </div>
            </aside>

            <div class="cart-products">
              <div class="cart-products__header">
                <h2 class="cart-products__title">
                  {{ $t('Товары') }}
                </h2>

                <span class="cart-products__amount">
                  {{ productsLabel }}
                </span>
              </div>

              <div class="product-list">
                <article
                  v-for="(cartItem, index) in $parent.cartContents"
                  :key="getCartItemKey(cartItem, index)"
                  class="cart-product"
                >
                  <div class="cart-product__preview">
                    <img
                      :src="getItemImage(cartItem)"
                      :alt="cartItem.item.title"
                      class="cart-product__image"
                    />
                  </div>

                  <div class="cart-product__content">
                    <h3 class="cart-product__name" :title="cartItem.item.title">
                      {{ cartItem.item.title }}
                    </h3>

                    <div class="cart-product__pricing">
                      <div class="cart-product__price">
                        {{ formatPrice(cartItem.item.price) }}
                        <span class="currency">{{ currency }}</span>
                      </div>

                      <div
                        v-if="cartItem.count > 1"
                        class="cart-product__quantity"
                      >
                        {{ cartItem.count }} {{ $t('шту.') }}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    class="cart-product__delete"
                    :aria-label="$t('Удалить товар')"
                    @click="removeItem(cartItem)"
                  >
                    <img
                      src="./../img/bin.svg"
                      alt=""
                      class="cart-product__delete-icon"
                    />
                  </button>
                </article>
              </div>
            </div>
          </template>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
export default {
  name: 'Cart',

  props: {
    textPageList: {
      type: Array,
      default: () => [],
    },

    textPagesLoading: {
      type: Boolean,
      default: false,
    },

    textPagesError: {
      type: String,
      default: '',
    },

    currency: {
      type: String,
      default: 'EUR',
    },

    checkoutLoading: {
      type: Boolean,
      default: false,
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['orderSubmit'],

  data() {
    return {
      imgDomain: '',

      selectedPaymentMethod: '',

      name: '',
      surname: '',
      email: '',
      phone: '',
      nickname: '',

      country: '',
      countryId: '',
      countryOptions: [],
      city: '',
      address: '',
      zipCode: '',

      terms: false,

      promo: '',
      promoError: '',
      promoErrorTimeout: null,
    }
  },

  computed: {
    hasCartItems() {
      return Boolean(
        Array.isArray(this.$parent.cartContents) &&
          this.$parent.cartContents.length,
      )
    },

    availablePaymentMethods() {
      return Array.isArray(this.settings?.available_payment_methods)
        ? this.settings.available_payment_methods
        : []
    },

    cartItemsCount() {
      if (!Array.isArray(this.$parent.cartContents)) {
        return 0
      }

      return this.$parent.cartContents.reduce((total, cartItem) => {
        const count = Number(cartItem.count)

        return total + (Number.isFinite(count) ? count : 0)
      }, 0)
    },

    termsPage() {
      return (
        this.textPageList.find((page) => {
          return (
            Number(page.is_terms) === 1 ||
            page.is_terms === true ||
            page.type === 'terms' ||
            page.slug === 'terms'
          )
        }) || null
      )
    },

    privacyPage() {
      return (
        this.textPageList.find((page) => {
          return (
            Number(page.is_privacy) === 1 ||
            page.is_privacy === true ||
            page.type === 'privacy' ||
            page.slug === 'privacy'
          )
        }) || null
      )
    },

    documentsAreAvailable() {
      return Boolean(this.termsPage && this.privacyPage)
    },

    requiredFieldsAreFilled() {
      return Boolean(
        !this.textPagesLoading &&
          this.documentsAreAvailable &&
          this.terms &&
          this.selectedPaymentMethod,
      )
    },

    productsLabel() {
      const count = this.cartItemsCount

      if (count % 10 === 1 && count % 100 !== 11) {
        return `${count} товар`
      }

      if (
        count % 10 >= 2 &&
        count % 10 <= 4 &&
        (count % 100 < 12 || count % 100 > 14)
      ) {
        return `${count} товара`
      }

      return `${count} товаров`
    },
  },

  watch: {
    availablePaymentMethods: {
      immediate: true,
      handler(methods) {
        if (!methods.length) {
          this.selectedPaymentMethod = ''
          return
        }

        const selectedStillExists = methods.some(
          (method) => method.code === this.selectedPaymentMethod,
        )

        if (!selectedStillExists) {
          this.selectedPaymentMethod = methods[0].code
        }
      },
    },

    country(newCountry) {
      const selectedCountry = this.countryOptions.find(
        (country) => country.title === newCountry,
      )

      this.countryId = selectedCountry ? selectedCountry.id : ''
    },

    currency() {
      this.refreshCart()
    },
  },

  mounted() {
    this.imgDomain = process.env.VUE_APP_STATIC_DOMAIN || ''

    this.getProfile()
    this.getCountries()
  },

  beforeUnmount() {
    if (this.promoErrorTimeout) {
      clearTimeout(this.promoErrorTimeout)
    }
  },

  methods: {
    retryTextPages() {
      if (this.$parent && typeof this.$parent.getTextPages === 'function') {
        this.$parent.getTextPages()
      }
    },
    refreshCart() {
      this.$parent?.getCartContents()
    },
    openStaticPage(page) {
      if (!page || !page.id) {
        return
      }

      if (this.$parent && typeof this.$parent.goToPage === 'function') {
        this.$parent.goToPage(page)
        return
      }

      const slug = String(page.title || '')
        .replace(/\//g, '')
        .trim()
        .replace(/\s+/g, '-')

      this.$router.push({
        path: `/pages/${slug}`,
      })
    },

    getCartItemKey(cartItem, index) {
      return (
        cartItem.id ||
        cartItem.item_id ||
        (cartItem.item && cartItem.item.id) ||
        `${cartItem.type || 'product'}-${index}`
      )
    },

    getItemImage(cartItem) {
      const item = (cartItem && cartItem.item) || {}

      const imageUrl = item.img_url || item.image_url || ''

      if (!imageUrl) {
        return ''
      }

      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl
      }

      const domain = this.imgDomain.replace(/\/+$/, '')

      const path = imageUrl.replace(/^\/+/, '')

      return domain ? `${domain}/${path}` : `/${path}`
    },

    formatPrice(price) {
      const value = Number(price)

      return Number.isFinite(value) ? value.toFixed(2) : '0.00'
    },

    getItemSubtotal(cartItem) {
      const item = (cartItem && cartItem.item) || {}

      const price = Number(item.price)
      const count = Number(cartItem.count)

      if (!Number.isFinite(price) || !Number.isFinite(count)) {
        return '0.00'
      }

      return (price * count).toFixed(2)
    },

    goToStore() {
      this.$router.push({
        path: '/categories',
      })
    },

    applyPromo() {
      if (!this.promo) {
        return
      }

      this.promoError = this.$t('Указанный промокод недействителен')

      if (this.promoErrorTimeout) {
        clearTimeout(this.promoErrorTimeout)
      }

      this.promoErrorTimeout = setTimeout(() => {
        this.promoError = ''
      }, 3000)
    },

    removeItem(cartItem) {
      if (!this.$parent || typeof this.$parent.removeFromCart !== 'function') {
        return
      }

      this.$parent.removeFromCart(
        {
          ...(cartItem.item || {}),
          type: cartItem.type,
        },
        cartItem.item_id,
      )
    },

    submit() {
      if (!this.requiredFieldsAreFilled || this.checkoutLoading) {
        return
      }

      this.$emit('orderSubmit', {
        name: this.name,
        surname: this.surname,
        // phone: this.phone,
        email: this.email,
        nickname: this.nickname,

        country: this.countryId || undefined,
        city: this.city || undefined,
        address: this.address || undefined,
        postCode: this.zipCode || undefined,

        promo: this.promo || undefined,
        paymentType: this.selectedPaymentMethod,
      })
    },

    getCountries() {
      this.$http
        .get('countries')
        .then((response) => {
          const payload = response && response.data && response.data.payload

          this.countryOptions = Array.isArray(payload) ? payload : []
        })
        .catch(() => {
          this.countryOptions = []
        })
    },

    getProfile() {
      this.$http
        .get('user/profile')
        .then((response) => {
          const profile = response.data || {}

          this.name = profile.name || ''
          this.surname = profile.surname || ''
          this.phone = profile.phone || ''
          this.email = profile.email || ''
          this.nickname = profile.nickname || ''
        })
        .catch((error) => {
          if (
            error.response &&
            error.response.status === 401 &&
            this.$parent &&
            typeof this.$parent.openSignIn === 'function'
          ) {
            this.$parent.openSignIn()
          }
        })
    },
  },
}
</script>

<style scoped lang="scss">
.cart-page {
  min-height: 50vh;
  background: var(--white-woodsmoke);
}

/* Header */

.cart-page .hero-section {
  padding: 44px 16px 24px;

  .wrapper {
    max-width: 1210px;
    padding: 0 16px;
  }

  .wrapper-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  .title-container {
    display: flex;
    align-items: center;
    gap: 16px;
  }
}

.cart-page__title {
  margin: 0;
  font-family: var(--font-2);
  font-size: 34px;
  font-weight: 700;
  line-height: 44px;
  color: var(--gray-nurse);
}

.clear-cart-button {
  min-height: 42px;
  justify-content: center;
  margin: 0;
  font-family: var(--font-1);
  color: var(--gray-nurse) !important;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
}

/* Main layout */

.cart-section {
  padding: 16px 16px 100px;

  > .wrapper {
    max-width: 1210px;
    display: grid;
    grid-template-columns:
      minmax(300px, 380px)
      minmax(0, 1fr);
    align-items: start;
    gap: 24px;
    padding: 24px 0 0;
  }
}

/* Empty cart */

.empty-cart {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 64px 24px;
  text-align: center;

  background: var(--bunker-86);
  border: 1px solid var(--outer-space);
  border-radius: 10px;

  box-shadow: inset 3px 3px 0 rgba(255, 255, 255, 0.025),
    inset -3px -3px 0 rgba(0, 0, 0, 0.22);
}

.empty-cart__icon {
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;

  color: var(--44);
  background: var(--heavy-metal);
  border: 1px solid var(--outer-space);
  border-radius: 10px;

  box-shadow: inset 3px 3px 0 rgba(255, 255, 255, 0.03),
    inset -3px -3px 0 rgba(0, 0, 0, 0.25);
}

.empty-cart__title {
  margin: 24px 0 0;
  font-family: var(--font-2);
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: var(--gray-nurse);
}

.empty-cart__description {
  max-width: 460px;
  margin: 12px 0 0;
  font-family: var(--font-1);
  font-size: 14px;
  line-height: 22px;
  color: var(--pewter);
}

.empty-cart__button {
  min-width: 220px;
  justify-content: center;
  margin-top: 28px;
  padding: 14px 20px;

  font-family: var(--font-3);
  font-size: 10px;
  line-height: 14px;
}

/* Summary */

.cart-summary {
  position: sticky;
  top: 24px;
  min-width: 0;
}

.cart-summary__card {
  position: relative;
  padding: 24px;

  background: var(--bunker-86);
  border: 1px solid var(--outer-space);
  border-radius: 10px;

  box-shadow: 6px 6px 0 #0c0f13;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    border-radius: inherit;
    pointer-events: none;

    box-shadow: inset 3px 3px 0 rgba(255, 255, 255, 0.025),
      inset -3px -3px 0 rgba(0, 0, 0, 0.22);
  }

  > * {
    position: relative;
    z-index: 1;
  }
}

.cart-summary__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--outer-space);
}

.cart-summary__title {
  margin: 0;
  font-family: var(--font-2);
  font-size: 20px;
  font-weight: 600;
  line-height: 26px;
  color: var(--gray-nurse);
}

.cart-summary__count {
  min-width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;

  font-family: var(--font-2);
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;

  color: var(--44);
  background: var(--heavy-metal);
  border: 1px solid var(--outer-space);
  border-radius: 7px;
}

.cart-summary__total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 0;
  border-bottom: 1px solid var(--outer-space);
}

.cart-summary__total-label {
  font-family: var(--font-1);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--pewter);
}

.cart-summary__total-price {
  display: flex;
  align-items: baseline;
  gap: 5px;

  font-family: var(--font-2);
  font-size: 22px;
  font-weight: 700;
  line-height: 28px;
  color: var(--harvest-gold);
  white-space: nowrap;

  .currency {
    font-size: 22px;
    font-weight: 700;
    line-height: 28px;
  }
}

/* Terms */

.cart-summary__terms {
  padding-top: 20px;
}

.terms-checkbox {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0;
  cursor: pointer;

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }

  input:checked + .terms-checkbox__box {
    color: var(--gray-nurse);
    background: var(--31);
    border-color: var(--44);

    svg {
      opacity: 1;
      transform: scale(1);
    }
  }

  input:focus-visible + .terms-checkbox__box {
    box-shadow: 0 0 0 3px rgba(33, 193, 126, 0.15);
  }
}

.terms-checkbox__box {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;

  color: transparent;
  background: var(--white-woodsmoke);
  border: 1px solid var(--outer-space);
  border-radius: 5px;

  box-shadow: inset 2px 2px 0 rgba(0, 0, 0, 0.35),
    inset -1px -1px 0 rgba(255, 255, 255, 0.04);

  transition: color 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease, box-shadow 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
    opacity: 0;
    transform: scale(0.7);
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
}

.terms-checkbox__text {
  font-family: var(--font-1);
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: var(--pewter);
}

.terms-checkbox__link {
  display: inline;
  margin: 0;
  padding: 0;

  font: inherit;
  font-weight: 600;
  line-height: inherit;
  color: var(--44);
  background: transparent;
  border: 0;

  @media (any-hover: hover) {
    &:hover {
      color: var(--gray-nurse);
      text-decoration: underline;
    }
  }
}

/* Checkout button */

.cart-summary__submit {
  width: 100%;
  min-height: 46px;
  justify-content: center;
  margin-top: 24px;
  padding: 12px 16px;

  font-family: var(--font-3);
  font-size: 10px;
  line-height: 14px;
}

/* Promo */

.promo {
  margin-top: 26px;
  padding-top: 22px;
  border-top: 1px solid var(--outer-space);
}

.promo__label {
  display: block;
  margin: 0 0 9px;

  font-family: var(--font-1);
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  color: var(--pewter);
}

.promo__controls {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
}

.promo__input {
  width: 100%;
  min-width: 0;
  height: 42px;
  margin: 0;
  padding: 10px 12px;

  font-family: var(--font-1);
  font-size: 13px;
  line-height: 20px;

  color: var(--gray-nurse);
  background: var(--white-woodsmoke);
  border: 1px solid var(--outer-space);
  border-radius: 8px;
  outline: none;

  box-shadow: inset 2px 2px 0 rgba(0, 0, 0, 0.35),
    inset -1px -1px 0 rgba(255, 255, 255, 0.03);

  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    font-size: 13px;
    color: var(--pewter);
    opacity: 0.65;
  }

  &:focus {
    border-color: var(--31);
    box-shadow: inset 2px 2px 0 rgba(0, 0, 0, 0.35),
      0 0 0 2px rgba(33, 193, 126, 0.12);
  }
}

.promo__button {
  min-width: 102px;
  min-height: 42px;
  justify-content: center;
  margin: 0;
  padding: 10px 14px;

  font-family: var(--font-1);
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
}

.cart-message {
  margin-top: 14px;
  padding: 10px 12px;

  font-family: var(--font-1);
  font-size: 12px;
  line-height: 18px;

  border-radius: 7px;

  &_error {
    color: #ff8b9e;
    background: rgba(255, 71, 102, 0.08);
    border: 1px solid rgba(255, 71, 102, 0.25);
  }
}

/* Products */

.cart-products {
  min-width: 0;
  padding: 24px;

  background: var(--bunker-86);
  border: 1px solid var(--outer-space);
  border-radius: 10px;

  box-shadow: inset 3px 3px 0 rgba(255, 255, 255, 0.025),
    inset -3px -3px 0 rgba(0, 0, 0, 0.22);
}

.cart-products__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--outer-space);
}

.cart-products__title {
  margin: 0;
  font-family: var(--font-2);
  font-size: 20px;
  font-weight: 600;
  line-height: 26px;
  color: var(--gray-nurse);
}

.cart-products__amount {
  font-family: var(--font-1);
  font-size: 13px;
  line-height: 18px;
  color: var(--pewter);
}

.product-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 18px;
}

.cart-product {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 86px;
  padding: 12px 14px;

  background: var(--white-woodsmoke);
  border: 1px solid var(--outer-space);
  border-radius: 8px;

  transition: background-color 0.25s ease, border-color 0.25s ease,
    transform 0.25s ease;

  @media (any-hover: hover) {
    &:hover {
      background: var(--heavy-metal);
      border-color: rgba(33, 193, 126, 0.35);
      transform: translateY(-1px);
    }
  }
}

.cart-product__preview {
  flex: 0 0 60px;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;

  background: radial-gradient(circle, #1d2723 20%, #121815 70%);

  border: 1px solid var(--outer-space);
  border-radius: 6px;

  box-shadow: inset 3px 3px 0 rgba(0, 0, 0, 0.45),
    inset -2px -2px 0 rgba(255, 255, 255, 0.04);
}

.cart-product__image {
  display: block;
  width: 44px;
  height: 44px;
  object-fit: contain;
  object-position: center;
}

.cart-product__content {
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.cart-product__name {
  min-width: 0;
  margin: 0;

  font-family: var(--font-2);
  font-size: 15px;
  font-weight: 600;
  line-height: 21px;
  color: var(--gray-nurse);

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cart-product__price {
  flex-shrink: 0;
  display: flex;
  align-items: baseline;
  gap: 4px;

  font-family: var(--font-2);
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  color: var(--harvest-gold);
  white-space: nowrap;

  .currency {
    color: var(--harvest-gold);
  }
}

.cart-product__delete {
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  background: var(--heavy-metal);
  border: 1px solid var(--outer-space);
  border-radius: 7px;

  transition: background-color 0.2s ease, border-color 0.2s ease,
    transform 0.2s ease;

  @media (any-hover: hover) {
    &:hover {
      background: rgba(255, 71, 102, 0.12);
      border-color: rgba(255, 71, 102, 0.45);
      transform: translateY(-1px);

      .cart-product__delete-icon {
        opacity: 1;
      }
    }
  }
}

.cart-product__delete-icon {
  display: block;
  width: 16px;
  height: 16px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

/* Transitions */

.slide-enter-active,
.slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.cart-product__pricing {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 4px;
}

.cart-product__quantity {
  display: block;
  visibility: visible;
  opacity: 1;

  font-family: var(--font-1);
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;

  color: var(--31);
  white-space: nowrap;
}

/* Responsive */

@media (max-width: 1100px) {
  .cart-section > .wrapper {
    grid-template-columns:
      minmax(280px, 340px)
      minmax(0, 1fr);
    gap: 18px;
  }

  .cart-summary__card,
  .cart-products {
    padding: 20px;
  }

  .cart-product__content {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }
}

@media (max-width: 900px) {
  .cart-section > .wrapper {
    display: flex;
    flex-direction: column;
  }

  .cart-products {
    width: 100%;
    order: 1;
  }

  .cart-summary {
    position: static;
    width: 100%;
    order: 2;
  }
}

@media (max-width: 767.98px) {
  .cart-page .hero-section {
    padding: 25px 16px 16px;

    .wrapper {
      padding: 0;
    }
  }

  .cart-page__title {
    font-size: 25px;
    line-height: 32px;
  }

  .back-page {
    width: 36px;
    height: 36px;
  }

  .clear-cart-button {
    padding: 10px 14px;
    font-size: 11px;
  }

  .cart-section {
    padding: 8px 16px 70px;

    > .wrapper {
      padding-top: 12px;
    }
  }

  .cart-summary__card,
  .cart-products {
    padding: 16px;
    border-radius: 8px;
  }

  .cart-product {
    padding: 10px;
  }

  .cart-product__preview {
    flex-basis: 52px;
    width: 52px;
    height: 52px;
  }

  .cart-product__image {
    width: 38px;
    height: 38px;
  }

  .cart-product__name {
    font-size: 14px;
    line-height: 19px;
  }

  .cart-product__price {
    font-size: 13px;
  }

  .cart-product__delete {
    flex-basis: 34px;
    width: 34px;
    height: 34px;
  }

  .promo__controls {
    grid-template-columns: 1fr;
  }

  .promo__button {
    width: 100%;
  }

  .empty-cart {
    padding: 48px 18px;
  }
}

@media (max-width: 540px) {
  .cart-page .hero-section .wrapper-top {
    align-items: stretch;
    flex-direction: column;
  }

  .clear-cart-button {
    width: 100%;
  }

  .cart-products__header {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .cart-product {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .cart-product__content {
    width: calc(100% - 66px);
  }

  .cart-product__name {
    white-space: normal;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }

  .cart-product__delete {
    margin-left: auto;
  }

  .cart-summary__total {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }
}

.cart-checkout-loader {
  width: 20px;
  height: 20px;
  display: block;

  border: 3px solid rgba(7, 15, 11, 0.28);
  border-top-color: var(--racing-green);
  border-radius: 50%;

  animation: cart-checkout-loader-spin 0.75s linear infinite;
}

.payment-methods {
  padding: 20px 0;
  border-bottom: 1px solid var(--outer-space);
}

.payment-methods__title {
  margin-bottom: 12px;

  font-family: var(--font-1);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--pewter);
}

.payment-methods__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.payment-method {
  width: 100%;
  min-height: 44px;
  padding: 10px 14px;

  font-family: var(--font-1);
  font-size: 13px;
  font-weight: 600;
  text-align: left;

  color: var(--gray-nurse);
  background: var(--white-woodsmoke);
  border: 1px solid var(--outer-space);
  border-radius: 8px;

  cursor: pointer;

  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.payment-method_active {
  background: var(--heavy-metal);
  border-color: var(--31);
}

@media (any-hover: hover) {
  .payment-method:hover {
    border-color: var(--31);
  }
}

@keyframes cart-checkout-loader-spin {
  to {
    transform: rotate(360deg);
  }
}

.terms-state {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 12px;

  font-family: var(--font-1);
  font-size: 12px;
  line-height: 19px;

  border-radius: 7px;
}

.terms-state_loading {
  align-items: center;

  color: var(--pewter);
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid var(--outer-space);
}

.terms-state_error {
  color: #ff9bad;
  background: rgba(255, 71, 102, 0.07);
  border: 1px solid rgba(255, 71, 102, 0.22);
}

.terms-state__loader {
  flex: 0 0 17px;
  width: 17px;
  height: 17px;

  border: 2px solid rgba(255, 255, 255, 0.12);
  border-top-color: var(--mountain-meadow);
  border-radius: 50%;

  animation: terms-loader-spin 0.75s linear infinite;
}

.terms-state__icon {
  flex: 0 0 21px;
  width: 21px;
  height: 21px;
  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 12px;
  font-weight: 700;
  line-height: 1;

  color: #ff9bad;
  background: rgba(255, 71, 102, 0.1);
  border-radius: 50%;
}

.terms-state__content {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 5px;
}

.terms-state__message {
  overflow-wrap: anywhere;
}

.terms-state__retry {
  margin: 0;
  padding: 0;

  font: inherit;
  font-weight: 700;

  color: var(--mountain-meadow);
  background: transparent;
  border: 0;

  @media (any-hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }
}

@keyframes terms-loader-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
